import type { Row } from "@tanstack/react-table";
import * as yup from "yup";

import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type {
	PendingInvoicesProjectPhaseType,
	PendingInvoicesProjectType,
	PendingProjectInvoice,
	StaffingClosingStatusesByMonth,
	UserClosingStatus,
} from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import type { PendingInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";

export const processedStatuses: InvoiceStatusId[] = [invoiceStatusId.Paid, invoiceStatusId.Reversed, invoiceStatusId.SentToClient];
export const wipStatuses: InvoiceStatusId[] = [invoiceStatusId.Draft, invoiceStatusId.AwaitingApproval, invoiceStatusId.Approved];

export function createPendingInvoiceTableDataFromProjectInvoice(
	invoiceData: PendingProjectInvoice,
	projectPhases: PendingInvoicesProjectPhaseType[],
	projects: PendingInvoicesProjectType[],
): PendingInvoicesIndexTableData {
	const project = projects.find((p) => p.id === invoiceData.projectId);

	const phases = invoiceData.projectPhaseIds.map((phaseId) => projectPhases.find((p) => p.id === phaseId)!);

	if (!project || !phases) {
		throw new Error("Missing project or project phase for invoice");
	}

	const projectNumberParts = project.projectNumber.split("-");
	const projectCounter = parseInt(projectNumberParts[projectNumberParts.length - 1]) || 0;
	let projectNumberWithPhaseNumber = project.projectNumber;

	if (phases.length === 1) {
		projectNumberWithPhaseNumber += " - " + phases[0]?.phaseNumber;
	}

	return {
		allStaffingsClosed: invoiceData.allStaffingsClosed,
		comment: invoiceData.comment,
		companyId: project.companyId,
		companyAvatarImage: project.companyAvatarImage,
		companyDisplayName: project.companyDisplayName || "",
		consecutiveInvoiceNumber: invoiceData.consecutiveInvoiceNumber,
		deliveryEndDate: new Date(invoiceData.deliveryEndDate),
		deliveryStartDate: new Date(invoiceData.deliveryStartDate),
		hasFreelancers: invoiceData.hasFreelancers,
		hasOverbookedOrder: invoiceData.hasOverbookedOrder,
		id: invoiceData.id,
		invoiceNumber: invoiceData.invoiceNumber || null,
		invoiceTypeId: invoiceData.invoiceTypeId,
		isGrossBased: false,
		isProjectInvoice: true,
		orderBillingInstructions: invoiceData.orderBillingInstructions,
		orderManagedBy: invoiceData.orderManagedBy || null,
		paymentTargetDays: invoiceData.paymentTargetDays || 0,
		orders: invoiceData.orders || [],
		phases: phases,
		projectBillingInstructions: invoiceData.projectBillingInstructions,
		projectCounter,
		projectId: invoiceData.projectId,
		projectNumberWithPhaseNumber,
		projectTitle: project.projectTitle,
		recipientInformation: null,
		staffingsClosingStatuses: invoiceData.staffingsClosingStatuses,
		subtotalCents: invoiceData.subtotalCents,
		totalAmountCents: invoiceData.totalAmountCents,
		vatPercentage: 0,
	};
}

export function projectNumberSortingFunction(
	a: Row<PendingInvoicesIndexTableData>,
	b: Row<PendingInvoicesIndexTableData>,
): number {
	const projectCounterA = a.original.projectCounter;
	const projectCounterB = b.original.projectCounter;
	return projectCounterA - projectCounterB;
}

export function getAggregatedStaffingClosingStatuses(
	monthlyClosingStatuses: StaffingClosingStatusesByMonth[],
): UserClosingStatus[] {
	const statusByUserId = new Map<string, UserClosingStatus>();

	for (const entry of monthlyClosingStatuses) {
		for (const status of entry.staffingClosingStatuses) {
			const userStatus = statusByUserId.get(status.userId)
				?? { userId: status.userId, hasClosed: true, unclosedMonths: [] };
			if (!status.hasClosed) {
				userStatus.hasClosed = false;
				if (!userStatus.unclosedMonths.includes(entry.month)) {
					userStatus.unclosedMonths.push(entry.month);
				}
			}
			statusByUserId.set(status.userId, userStatus);
		}
	}

	return Array.from(statusByUserId.values());
}

type WithInvoiceNumberSortFields = {
	consecutiveInvoiceNumber: number | null;
	invoiceNumber: string | null;
};

const parseInvoiceNumber = (invoiceNumber: string) => {
	const parts = invoiceNumber.split("-");
	if (parts.length >= 4) {
		return {
			year: parseInt(parts[1]),
			project: parseInt(parts[2]),
			counter: parseInt(parts[3]),
		};
	}
	return null;
};

export function sortByInvoiceNumber<T extends WithInvoiceNumberSortFields>(
	rowA: Row<T>,
	rowB: Row<T>,
): number {
	const consecutiveA = rowA.original.consecutiveInvoiceNumber;
	const consecutiveB = rowB.original.consecutiveInvoiceNumber;
	const invoiceA = rowA.original.invoiceNumber;
	const invoiceB = rowB.original.invoiceNumber;

	if (!invoiceA && !invoiceB) return 0;
	if (!invoiceA) return -1;
	if (!invoiceB) return 1;

	const hasConsecutiveA = consecutiveA !== null;
	const hasConsecutiveB = consecutiveB !== null;

	if (hasConsecutiveA && !hasConsecutiveB) return 1;
	if (!hasConsecutiveA && hasConsecutiveB) return -1;

	if (hasConsecutiveA && hasConsecutiveB) {
		return consecutiveA - consecutiveB;
	}

	const parsedA = parseInvoiceNumber(invoiceA);
	const parsedB = parseInvoiceNumber(invoiceB);

	if (!parsedA && !parsedB) return 0;
	if (!parsedA) return -1;
	if (!parsedB) return 1;

	if (parsedA.year !== parsedB.year) return parsedA.year - parsedB.year;
	if (parsedA.project !== parsedB.project) return parsedA.project - parsedB.project;
	return parsedA.counter - parsedB.counter;
}

export function getInvoiceTotalsSchema(requireNegative: boolean) {
	const amountConstraint = (schema: yup.NumberSchema<number, yup.AnyObject, 0, "d">, label: string) => {
		return requireNegative
			? schema.max(-1, `${label} muss negativ sein.`)
			: schema.min(1, `${label} muss positiv sein.`);
	};

	return {
		isGrossBased: yup.boolean().required().default(false),
		subtotalCents: yup.number().default(0).when("isGrossBased", {
			is: false,
			then: (schema) => amountConstraint(schema.required(), "Der Nettobetrag"),
			otherwise: (schema) => schema.optional(),
		}),
		totalAmountCents: yup.number().default(0).when("isGrossBased", {
			is: true,
			then: (schema) => amountConstraint(schema.required(), "Der Bruttobetrag"),
			otherwise: (schema) => schema.optional(),
		}),
		travelCostCentsBeforeVat: yup.number().required(),
		vatPercentage: yup.number().required(),
	};
}
